<script setup lang="ts">
/**
 * Access tests for one assistant (ADR 0066): the same question asked as
 * different people. Each test names what the answer turns up — a document
 * title or path, or `table:<name>` — and, per persona, whether they should
 * find it. A run asks through the chat path's own access code, plus once
 * as an admin: if even that does not find it, the check proves nothing and
 * says so.
 *
 * "Show answers" asks the question for real, as each persona, and puts the
 * answers side by side — the view that convinces people in a review
 * meeting. Never stored: an answer carries the data.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref, watch } from "vue";
import {
  AdminError,
  type AccessTest,
  type AccessTestPersona,
  type AccessTestResult,
  type AccessTestsView,
  type Expect,
  type PersonaAnswer,
  type SuggestedTest,
  getAccessTestAnswers,
  getAccessTests,
  retireAccessTest,
  runAccessTests,
  saveAccessTest,
  suggestAccessTests,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { fmtTs } from "../utils/governance";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ slug: string; devUser: string }>();
const emit = defineEmits<{ (e: "changed"): void }>();

const toast = useToast();
const { confirm } = useConfirm();

const view = ref<AccessTestsView | null>(null);
const loading = ref(false);
const busy = ref<"" | "run" | "save" | "suggest" | number>("");

function fail(e: unknown, what: string): void {
  toast.error(what, e instanceof AdminError ? e.message : String(e));
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    view.value = await getAccessTests(props.slug, props.devUser);
  } catch (e) {
    fail(e, "Could not load access tests");
  } finally {
    loading.value = false;
  }
}

function apply(v: AccessTestsView): void {
  view.value = v;
  emit("changed");
}

async function run(): Promise<void> {
  busy.value = "run";
  try {
    apply(await runAccessTests(props.slug, props.devUser));
    const s = view.value!.summary;
    if (s.ok) toast.success(s.detail);
    else toast.error("Access tests", s.detail);
  } catch (e) {
    fail(e, "Could not run the tests");
  } finally {
    busy.value = "";
  }
}

const STATE_TONE = {
  not_needed: "muted",
  missing: "warn",
  not_run: "warn",
  stale: "warn",
  failing: "bad",
  inconclusive: "warn",
  passing: "ok",
} as const;
const STATE_LABEL = {
  not_needed: "not needed",
  missing: "missing",
  not_run: "not run",
  stale: "out of date",
  failing: "failing",
  inconclusive: "proves nothing",
  passing: "passing",
} as const;

const resultsFor = computed(() => {
  const m = new Map<string, AccessTestResult>();
  for (const r of view.value?.run?.results ?? [])
    m.set(`${r.test_id}|${r.persona}`, r);
  return m;
});

function labelOf(spec: string): string {
  return (
    view.value?.personas.find((p) => p.as === spec)?.label ??
    (spec.startsWith("team:") ? `Team ${spec.slice(5)}` : spec)
  );
}

function outcomeText(r: AccessTestResult | undefined): string {
  if (!r) return "not run";
  if (r.verdict === "inconclusive")
    return r.outcome === "unknown_persona"
      ? "team no longer exists"
      : "question does not find it, even for an admin";
  if (r.outcome === "sees") return "finds it";
  if (r.outcome === "cannot_open") return "cannot open the assistant";
  return "does not find it";
}

// ── Form ──────────────────────────────────────────────────────────────────

type Draft = {
  id: number | null;
  question: string;
  look_for: string;
  note: string;
  personas: AccessTestPersona[];
};
const draft = ref<Draft | null>(null);

function newDraft(): void {
  const mayUse = view.value?.personas.find(
    (p) => p.may_use && p.as !== "everyone",
  );
  draft.value = {
    id: null,
    question: "",
    look_for: "",
    note: "",
    personas: [
      ...(mayUse ? [{ as: mayUse.as, expect: "sees" as Expect }] : []),
      { as: "everyone", expect: "blocked" },
    ],
  };
}
function editDraft(t: AccessTest): void {
  draft.value = {
    id: t.id,
    question: t.question,
    look_for: t.look_for,
    note: t.note,
    personas: t.personas.map((p) => ({ ...p })),
  };
}
function closeForm(): void {
  draft.value = null;
}
function addPersona(): void {
  const used = new Set(draft.value!.personas.map((p) => p.as));
  const next = view.value?.personas.find((p) => !used.has(p.as));
  if (next) draft.value!.personas.push({ as: next.as, expect: "blocked" });
}

async function save(): Promise<void> {
  const d = draft.value;
  if (!d) return;
  busy.value = "save";
  try {
    apply(
      await saveAccessTest(
        props.slug,
        d.id,
        {
          question: d.question.trim(),
          look_for: d.look_for.trim(),
          note: d.note.trim(),
          personas: d.personas,
        },
        props.devUser,
      ),
    );
    closeForm();
    toast.success("Test saved — run it to see the result");
  } catch (e) {
    fail(e, "Could not save the test");
  } finally {
    busy.value = "";
  }
}

async function retire(t: AccessTest): Promise<void> {
  const ok = await confirm({
    title: "Remove this test?",
    message: `“${t.question}” stops being checked. Past runs keep their record.`,
    confirmLabel: "Remove",
    danger: true,
  });
  if (!ok) return;
  busy.value = t.id;
  try {
    apply(await retireAccessTest(props.slug, t.id, props.devUser));
  } catch (e) {
    fail(e, "Could not remove the test");
  } finally {
    busy.value = "";
  }
}

// ── Answers side by side ──────────────────────────────────────────────────

const answers = ref<Record<number, PersonaAnswer[] | "loading">>({});

async function showAnswers(t: AccessTest): Promise<void> {
  answers.value = { ...answers.value, [t.id]: "loading" };
  try {
    const r = await getAccessTestAnswers(props.slug, t.id, props.devUser);
    answers.value = { ...answers.value, [t.id]: r.answers };
  } catch (e) {
    const { [t.id]: _drop, ...rest } = answers.value;
    answers.value = rest;
    fail(e, "Could not get the answers");
  }
}
function hideAnswers(id: number): void {
  const { [id]: _drop, ...rest } = answers.value;
  answers.value = rest;
}

// ── Suggestions from the real data ────────────────────────────────────────

const suggestions = ref<SuggestedTest[] | null>(null);

async function suggest(): Promise<void> {
  busy.value = "suggest";
  try {
    suggestions.value = (
      await suggestAccessTests(props.slug, props.devUser)
    ).suggestions;
    if (!suggestions.value.length)
      toast.success(
        "Nothing new to suggest",
        "No other restricted document or table here makes a check that proves something.",
      );
  } catch (e) {
    fail(e, "Could not suggest tests");
  } finally {
    busy.value = "";
  }
}

async function addSuggestion(sg: SuggestedTest): Promise<void> {
  busy.value = "save";
  try {
    apply(await saveAccessTest(props.slug, null, sg.test, props.devUser));
    suggestions.value = (suggestions.value ?? []).filter((x) => x !== sg);
    if (!suggestions.value.length) {
      suggestions.value = null;
      await run();
    }
  } catch (e) {
    fail(e, "Could not add the test");
  } finally {
    busy.value = "";
  }
}

// Last, so everything it resets is declared before its first (immediate) run.
watch(
  () => props.slug,
  () => {
    view.value = null;
    answers.value = {};
    suggestions.value = null;
    closeForm();
    void load();
  },
  { immediate: true },
);
</script>

<template>
  <div class="tests">
    <p class="hint">
      The same question asked as different people: who should
      <strong>find</strong> the answer, who should be <strong>kept out</strong>.
      Runs by itself on every change and weekly.
    </p>

    <div v-if="view" class="tests__bar">
      <StatusChip :tone="STATE_TONE[view.summary.state]" dot>
        {{ STATE_LABEL[view.summary.state] }}
      </StatusChip>
      <span class="small">{{ view.summary.detail }}</span>
      <span v-if="view.run" class="muted small">
        Last run {{ fmtTs(view.run.ran_at) }}
        <template v-if="view.run.ran_by">by {{ view.run.ran_by }}</template>
      </span>
      <span class="grow" />
      <button
        v-if="view.tests.length"
        class="btn btn--ghost"
        :disabled="!!busy"
        v-write
        @click="run"
      >
        {{ busy === "run" ? "Running…" : "Run now" }}
      </button>
      <button v-write class="btn btn--ghost" :disabled="!!busy" @click="suggest">
        {{ busy === "suggest" ? "Looking…" : "Suggest from the data" }}
      </button>
      <button
        v-if="!draft"
        class="btn btn--primary"
        :disabled="!!busy"
        v-write
        @click="newDraft"
      >
        Add a test
      </button>
    </div>

    <div v-if="suggestions?.length" class="suggest">
      <p class="small">
        <strong>Suggested from this assistant's own documents.</strong>
        Each has been run once; add the ones you want.
      </p>
      <div v-for="(sg, i) in suggestions" :key="i" class="suggest__item">
        <div class="suggest__main">
          <strong>“{{ sg.test.question }}”</strong>
          <span class="muted small">{{ sg.test.note }}</span>
          <span
            v-for="r in sg.results"
            :key="r.persona"
            :class="['small', `verdict--${r.verdict}`]"
          >
            {{ { pass: "✓", fail: "✗", inconclusive: "?" }[r.verdict] }}
            {{ r.persona_label }} —
            {{ r.expect === "sees" ? "should find it" : "should be kept out" }}:
            {{ outcomeText(r) }}
          </span>
        </div>
        <button
          class="btn btn--primary"
          :disabled="!!busy"
          v-write
          @click="addSuggestion(sg)"
        >
          Add
        </button>
      </div>
      <button class="link" @click="suggestions = null">Dismiss</button>
    </div>
    <p v-else-if="loading" class="muted small">Loading…</p>

    <form v-write v-if="draft" class="form" @submit.prevent="save">
      <label class="field">
        Question
        <input
          v-model="draft.question"
          maxlength="500"
          placeholder="What was our price in the Region H tender?"
          required
        />
      </label>
      <label class="field">
        What the answer turns up
        <input
          v-model="draft.look_for"
          maxlength="300"
          placeholder="Region H — or table:fact_contract_extract"
          required
        />
        <small>
          Text in the matching document's title or path, or
          <code>table:</code> and a table name.
        </small>
      </label>
      <div class="field">
        Who should find it
        <div v-for="(p, i) in draft.personas" :key="i" class="persona-row">
          <select v-model="p.as">
            <option v-for="o in view?.personas ?? []" :key="o.as" :value="o.as">
              {{ o.label }}{{ o.may_use ? "" : " (cannot open it today)" }}
            </option>
          </select>
          <select v-model="p.expect">
            <option value="sees">should find it</option>
            <option value="blocked">should be kept out</option>
          </select>
          <button
            type="button"
            class="link"
            :disabled="draft.personas.length === 1"
            @click="draft.personas.splice(i, 1)"
          >
            remove
          </button>
        </div>
        <button type="button" class="link" @click="addPersona">
          + another person
        </button>
      </div>
      <label class="field">
        Note (optional)
        <input v-model="draft.note" maxlength="500" placeholder="Why this check" />
      </label>
      <div class="form__actions">
        <button type="button" class="btn btn--ghost" @click="closeForm">
          Cancel
        </button>
        <button class="btn btn--primary" :disabled="busy === 'save'">
          {{ busy === "save" ? "Saving…" : "Save test" }}
        </button>
      </div>
    </form>

    <ul v-if="view?.tests.length" class="list">
      <li v-for="t in view.tests" :key="t.id" class="test">
        <div class="test__head">
          <strong class="test__q">“{{ t.question }}”</strong>
          <span class="muted small">
            looks for <code>{{ t.look_for }}</code>
            <template v-if="t.created_by"> · by {{ t.created_by }}</template>
          </span>
        </div>
        <table class="checks">
          <tbody>
            <tr v-for="p in t.personas" :key="p.as">
              <td>{{ labelOf(p.as) }}</td>
              <td class="muted">
                {{ p.expect === "sees" ? "should find it" : "should be kept out" }}
              </td>
              <td>
                <span
                  :class="[
                    'verdict',
                    `verdict--${resultsFor.get(`${t.id}|${p.as}`)?.verdict ?? 'none'}`,
                  ]"
                >
                  {{
                    { pass: "✓", fail: "✗", inconclusive: "?" }[
                      resultsFor.get(`${t.id}|${p.as}`)?.verdict ?? "inconclusive"
                    ]
                  }}
                  {{ outcomeText(resultsFor.get(`${t.id}|${p.as}`)) }}
                </span>
                <span
                  v-if="resultsFor.get(`${t.id}|${p.as}`)?.found?.length"
                  class="muted small"
                >
                  — {{ resultsFor.get(`${t.id}|${p.as}`)!.found!.join(", ") }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="test__actions">
          <button
            v-if="!answers[t.id]"
            class="link"
            :disabled="t.look_for.startsWith('table:')"
            :title="
              t.look_for.startsWith('table:')
                ? 'Table checks have no written answer to compare'
                : 'Ask for real as each person; costs one model turn each'
            "
            v-write
            @click="showAnswers(t)"
          >
            Show answers side by side
          </button>
          <button v-else class="link" @click="hideAnswers(t.id)">
            Hide answers
          </button>
          <button v-write class="link" @click="editDraft(t)">Edit</button>
          <button v-write class="link link--danger" :disabled="busy === t.id" @click="retire(t)">
            Remove
          </button>
        </div>
        <p v-if="answers[t.id] === 'loading'" class="muted small">
          Asking as each person…
        </p>
        <div v-else-if="answers[t.id]" class="answers">
          <div
            v-for="a in answers[t.id] as PersonaAnswer[]"
            :key="a.persona"
            class="answer"
          >
            <div class="answer__who">
              {{ a.persona_label }}
              <span class="muted small">
                ({{ a.expect === "sees" ? "should find it" : "should be kept out" }})
              </span>
            </div>
            <p v-if="a.note" class="muted">{{ a.note }}</p>
            <p v-else class="answer__text">{{ a.answer }}</p>
            <p v-if="a.citations?.length" class="muted small">
              Sources: {{ a.citations.join(" · ") }}
            </p>
          </div>
          <p class="muted small">
            Real answers, not stored. Only admins see this view.
          </p>
        </div>
      </li>
    </ul>
    <p v-else-if="view && !draft" class="muted small">
      No tests yet. A good first one: something from a restricted source that
      someone outside the audience asks for.
    </p>
  </div>
</template>

<style scoped>
.tests {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}
.hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
  line-height: 1.4;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.8rem;
}
.tests__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.grow {
  flex: 1;
}
.suggest {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: var(--space-3);
  border: 1px dashed var(--color-accent);
  border-radius: var(--radius-sm);
}
.suggest p {
  margin: 0;
}
.suggest__item {
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
  align-items: flex-start;
}
.suggest__main {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  overflow-wrap: anywhere;
}
.suggest .link {
  align-self: flex-start;
}
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.field input,
.field select,
.persona-row select {
  font-size: 0.92rem;
  font-family: inherit;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
.persona-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}
.persona-row select:first-child {
  flex: 1 1 12rem;
}
.form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.test {
  border-top: 1px solid var(--color-border);
  padding-top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}
.test__head {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.test__q {
  overflow-wrap: anywhere;
}
.checks {
  border-collapse: collapse;
  font-size: 0.85rem;
  width: 100%;
}
.checks td {
  padding: 0.2rem 0.6rem 0.2rem 0;
  vertical-align: top;
}
.verdict {
  font-weight: 600;
}
.verdict--pass {
  color: var(--color-success);
}
.verdict--fail {
  color: var(--color-error);
}
.verdict--inconclusive {
  color: var(--color-warn-ink);
}
.verdict--none {
  color: var(--color-muted);
  font-weight: 400;
}
.test__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}
.link {
  background: none;
  border: 0;
  padding: 0;
  color: var(--color-accent);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
}
.link:disabled {
  color: var(--color-muted);
  cursor: default;
}
.link--danger {
  color: var(--color-error);
}
.answers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: var(--space-2);
}
.answers > p {
  grid-column: 1 / -1;
  margin: 0;
}
.answer {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}
.answer p {
  margin: 0;
}
.answer__who {
  font-weight: 600;
}
.answer__text {
  white-space: pre-wrap;
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}
</style>
